import React from "react";
import { useKycNameEditor } from "../../hooks/useKycNameEditor";

interface KycNameFieldProps {
    label?: string;
    idLabel?: string;
}

// Reassurance banner + edit modal, since a name that doesn't match the ID
// document below will fail KYC verification.
export const KycNameField: React.FC<KycNameFieldProps> = ({
    label = "Name on file",
    idLabel = "ID document",
}) => {
    const {
        isEditingName,
        draftName,
        setDraftName,
        startEditingName,
        cancelEditingName,
        saveName,
        isSavingName,
    } = useKycNameEditor();

    return (
        <>
            <div className="space-y-2">
                <p className="text-xs font-medium text-gray-500">{label}</p>
                <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-md px-3 py-2.5 text-xs text-blue-800">
                    <span className="mt-0.5 text-blue-500 shrink-0">ℹ️</span>
                    <p>
                        Made a mistake when you signed up? Your name must match your {idLabel} exactly for KYC to pass.{" "}
                        <button type="button" onClick={startEditingName} className="font-semibold underline">
                            Edit your name
                        </button>{" "}
                        if it doesn't match.
                    </p>
                </div>
            </div>

            {isEditingName && (
                <div className="fixed inset-0 z-[10020] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm px-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
                        <div>
                            <h3 className="text-lg font-bold text-gray-800">Edit Your Name</h3>
                            <p className="text-xs text-gray-500 mt-1">
                                This must exactly match the name on your {idLabel} — mismatches can fail KYC verification.
                            </p>
                        </div>

                        <input
                            type="text"
                            autoFocus
                            value={draftName}
                            onChange={(e) => setDraftName(e.target.value)}
                            placeholder="Enter your full name as it appears on your ID"
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800"
                        />

                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={cancelEditingName}
                                className="px-4 py-2 rounded-md border text-sm text-gray-600 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={saveName}
                                disabled={isSavingName}
                                className="px-4 py-2 rounded-md bg-[#143887] text-white text-sm font-medium hover:bg-blue-800 disabled:opacity-60"
                            >
                                {isSavingName ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default KycNameField;
